// @givmo/fundraisers-module — every line the email designer shows (EMC-WEB10, D-EMC-20 (a)), from
// Jonathan's mockups 14a–14d (`Email Campaign Plan/scratchpad/…/mockups/14a-prompt.png` … `14d-small-revision.png`),
// copied from the frames character for character where they draw a line. Each says what it is: DRAWN (on a
// frame), or NEW (the frames draw no such state; written in their voice).
//
// SERVED words are the backend's, never copied here: every reply, its "Changes:" line, every refusal, the
// email itself (the server's render), the consent sheet.
//
// ⚠ The module's copy rail reads this file's FULL text, comments included (`copy-law.test.ts`): a test email
// that went out is "emailed to you", never the truth-gated word.

export const EMAIL_DESIGNER_COPY = {
  /** DRAWN (14a): the back link's fallback while the campaign's name loads. */
  backFallback: 'Campaign',
  /** NEW: the title of an email with no subject yet. */
  untitled: 'New email',
  /** DRAWN (14a): "Email 4 of 9". */
  position: (index: number, total: number) => `Email ${index} of ${total}`,
  /** DRAWN (14b): the save mark beside the time. */
  saved: 'Saved',
  /** DRAWN (14a). */
  sendTest: 'Send test',
  /** DRAWN (14a). */
  continue: 'Continue',
  /** NEW: the test's toast, the address the server answered. */
  testEmailed: (to: string) => `Test emailed to you at ${to}`,
  /** NEW: a test the server didn't take, before its own words. */
  testRefused: 'The test didn’t go out.',
  /** DRAWN (14c). */
  undo: 'Undo',
  /** NEW. */
  redo: 'Redo',
  /** NEW: the toast after an undo. */
  undone: 'Your last change was undone.',
  /** NEW (the polish-1 user test): the conversation's line after an Undo, so it never ends on a change
   *  the email no longer holds. */
  undoneLine: 'Undone. The email is back to how it was before that change.',
  /** NEW: an undo the server refused. */
  undoFailed: 'That change couldn’t be undone.',
  /** NEW: the email changed somewhere else since the assistant's last change, so Undo would overwrite it. */
  changedSince: 'This email changed since; reload to undo.',
  /** DRAWN (14a): the empty email's question. */
  emptyTitle: 'What should this email say?',
  /** DRAWN (14a). */
  emptyBody: 'Describe it in your own words. You can change anything after, the same way.',
  /** NEW: an email with words in it and no conversation yet (a duplicate, or one written elsewhere). */
  filledTitle: 'What would you like to change?',
  /** NEW. */
  filledBody: 'Describe it here, or click any words in the email to edit them.',
  /** NEW: the box's placeholder before the first draft (14a draws it typed into). */
  firstPlaceholder: 'Describe the email…',
  /** DRAWN (14b). */
  changePlaceholder: 'Describe a change…',
  /** NEW: the send control's accessible name. */
  send: 'Send to the assistant',
  /** NEW: the stop control's accessible name. */
  stop: 'Stop',
  /** DRAWN (14a): the line under the box. */
  connectorLine: 'You can also do this from Claude or ChatGPT if you’ve connected Givmo.',
  /** DRAWN (14b): the label a reply's facts line opens with. */
  changesLabel: 'Changes:',
  /** NEW: while a reply is on its way. */
  working: 'Working on it…',
  /** NEW: a turn that failed. */
  failed: 'The assistant couldn’t finish that.',
  /** NEW. */
  tryAgain: 'Try again',
  /** NEW: the organization has no accepted assistant sheet and no sheet to accept here. */
  unavailable: 'The assistant isn’t available for your organization yet.',
  /** NEW: the ask the organization's consent is holding, for a person who can turn the assistant on. */
  consentNeeded: (orgName: string) => `The assistant needs to be turned on for ${orgName} first.`,
  /** NEW: reopens the organization's sheet. */
  turnOn: 'Turn on the assistant',
  /** NEW: a person who can't accept the sheet for the organization. */
  needsAdmin: (orgName: string) =>
    `An admin who manages ${orgName} needs to turn on the assistant first.`,
  /** NEW: an email that can't be changed any more (it's on its way, or delivered). */
  locked: 'This email can’t be changed now.',
  /** DRAWN (14a): the preview card's title. */
  preview: 'Preview',
  /** DRAWN (14a). */
  desktop: 'Desktop',
  /** DRAWN (14a). */
  phone: 'Phone',
  /** NEW: the toggle's accessible name. */
  previewWidth: 'Preview width',
  /** DRAWN (14a). */
  inTheInbox: 'In the inbox',
  /** DRAWN (14a): the inbox line before a subject. */
  inboxEmpty: 'The subject and preview text appear here.',
  /** DRAWN (14a): the empty email's placeholder, inside the email. */
  emailEmptyTitle: 'Your email appears here',
  /** DRAWN (14a). */
  emailEmptyBody: (orgShort: string) =>
    `The assistant drafts it in ${orgShort}’s colors, logo and voice, with photos from your Collateral.`,
  /** DRAWN (14b–14d): the greeting's merge field, in place of a recipient's first name. */
  firstNameChip: 'First name',
  /** DRAWN (14a): the chip on Givmo's footer. */
  footerChip: 'Added by Givmo · Always included',
  /** NEW: the preview while the server renders. */
  previewLoading: 'Loading the preview…',
  /** NEW: an email this version can't show. */
  previewUnreadable: 'This email can’t be shown here.',
  /** NEW: the page's own unavailable state. */
  notAvailable: 'This email isn’t available',
  /** NEW. */
  backToCampaign: 'Back to the campaign',
  /** NEW (D-EMC-20 revised, 2026-10-31): the hand editor over the preview. Its bar after Mailchimp's and
   *  Klaviyo's inline text editing, kept to the four things a person tweaks. */
  hand: {
    /** A block's name, by its kind, in its controls' names. */
    kinds: {
      heading: 'heading',
      paragraph: 'paragraph',
      list: 'list',
      button: 'button',
      image: 'photo',
      divider: 'divider',
      spacer: 'space',
      columns: 'columns',
      employer_match: 'employer match note',
      block: 'block',
    } as Record<string, string>,
    blockLabel: (kind: string) => `Email ${kind}. Press Enter to edit its words.`,
    editing: (kind: string) => `Editing the ${kind}`,
    /** On an HTML block, the AI's alone. */
    askAssistant: 'Ask the assistant to change this part',
    lockedBlock: 'Ask the assistant to change this part.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    swapImage: 'Replace photo',
    remove: 'Delete',
    barLabel: 'Text formatting',
    bold: 'Bold',
    italic: 'Italic',
    link: 'Link',
    firstName: 'Insert first name',
    cancel: 'Cancel',
    done: 'Done',
    linkTitle: 'Link to',
    linkPlaceholder: 'Web address, email or phone',
    linkInvalid: 'Enter a web address (https://…), an email address or a phone number.',
    linkToAppeal: 'Link to the appeal page instead',
    linkIsAppeal: 'Links to the appeal page',
    /** Where the link at the caret goes, in the bar (the KCC hand edit): its address, or one of these. */
    linkAppealPage: 'The appeal page',
    linkJoinPage: 'The appeal’s join page',
    /** The words a link to the appeal takes when nothing was selected. */
    appealWords: 'our appeal page',
    linkApply: 'Apply',
    linkRemove: 'Remove link',
    firstNameFallback: 'If we don’t have their name, say',
    firstNameDefault: 'there',
    firstNameInsert: 'Insert',
    emptyWords: 'Type some words, or delete this block.',
    emptyButton: 'A button needs words.',
    longButton: (max: number) => `Keep a button’s words to ${max} characters.`,
    /** The server's own words follow when it says why. */
    refused: 'That change wasn’t saved.',
    pickerTitle: 'Replace photo',
    pickerBody: 'Choose a photo or graphic from your organization’s Collateral.',
    pickerUse: 'Use this photo',
    subject: 'Subject',
    preheader: 'Preview text',
    subjectPlaceholder: 'Add a subject',
    preheaderPlaceholder: 'Add preview text',
    editLine: (label: string) => `Edit the ${label.toLowerCase()}`,
    /** Under the preview's title: the hand editor, said once. */
    hint: 'Click any words to edit them.',
    /** Words left open while a change from elsewhere redrew the email. */
    overwritten: 'Someone else changed this part, so your words weren’t saved.',
  },
} as const;
